import { ActionList, Button, Checkbox, Popover, Select } from '@shopify/polaris';
import { DragHandleMinor } from '@shopify/polaris-icons';
import { useState } from 'react';
import { DIMENSION_BY_KEY, MAX_COL_LEVELS, MAX_ROW_LEVELS, MEASURES, MONEY_SCALES } from './constants';
import { HEADER_BG, INK, INK_FAINT, INK_SOFT, LINE, LINE_STRONG, LINK } from './pivotStyles';

const zoneLabel = { fontSize: 11, fontWeight: 600, color: INK_SOFT, letterSpacing: 0.4, textTransform: 'uppercase' };
const chipBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  height: 24,
  padding: '0 8px 0 3px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
  color: INK,
  background: '#FFFFFF',
  border: `1px solid ${LINE_STRONG}`,
  cursor: 'grab',
  whiteSpace: 'nowrap',
};
const linkBtn = {
  background: 'none',
  border: 0,
  padding: 0,
  color: LINK,
  fontSize: 13,
  cursor: 'pointer',
};

/** Ficha de una dimensión colocada. Clic: menú (mover, pasar al otro eje, quitar). Se puede arrastrar. */
const DimChip = ({ dimKey, zone, index, count, onMove, otherZoneFull }) => {
  const [open, setOpen] = useState(false);
  const other = zone === 'rows' ? 'cols' : 'rows';
  const items = [
    index > 0 && { content: 'Mover antes', onAction: () => onMove(dimKey, zone, index - 1) },
    index < count - 1 && { content: 'Mover después', onAction: () => onMove(dimKey, zone, index + 1) },
    !otherZoneFull && {
      content: zone === 'rows' ? 'Pasar a columnas' : 'Pasar a filas',
      onAction: () => onMove(dimKey, other),
    },
    { content: 'Quitar', destructive: true, onAction: () => onMove(dimKey, 'available') },
  ].filter(Boolean);

  return (
    <Popover
      active={open}
      onClose={() => setOpen(false)}
      activator={
        <button
          type="button"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', dimKey);
            e.dataTransfer.effectAllowed = 'move';
          }}
          onClick={() => setOpen((v) => !v)}
          style={chipBase}
          title="Clic para mover o quitar; también se puede arrastrar"
        >
          <DragHandleMinor aria-hidden width={14} height={14} fill={INK_FAINT} style={{ flex: 'none' }} />
          {DIMENSION_BY_KEY[dimKey].label}
          <svg aria-hidden width="10" height="10" viewBox="0 0 10 10" style={{ marginLeft: 2 }}>
            <path d="M2 3.5 L5 6.5 L8 3.5" fill="none" stroke={INK_SOFT} strokeWidth="1.5" />
          </svg>
        </button>
      }
    >
      <ActionList
        actionRole="menuitem"
        items={items.map((it) => ({
          ...it,
          onAction: () => {
            setOpen(false);
            it.onAction();
          },
        }))}
      />
    </Popover>
  );
};

const AddDim = ({ available, onAdd, disabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      active={open}
      onClose={() => setOpen(false)}
      activator={
        <button
          type="button"
          disabled={disabled || available.length === 0}
          onClick={() => setOpen((v) => !v)}
          style={{
            ...chipBase,
            padding: '0 8px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: 'transparent',
            borderStyle: 'dashed',
            color: INK_SOFT,
            fontWeight: 400,
            opacity: disabled || available.length === 0 ? 0.5 : 1,
          }}
          title={disabled ? 'Máximo de niveles alcanzado' : 'Añadir dimensión'}
        >
          + Añadir
        </button>
      }
    >
      <ActionList
        items={available.map((k) => ({
          content: DIMENSION_BY_KEY[k].label,
          onAction: () => {
            setOpen(false);
            onAdd(k);
          },
        }))}
      />
    </Popover>
  );
};

/** Zona de soltar: una lista ordenada de fichas. Al soltar sobre una ficha, se inserta en su sitio. */
const Zone = ({ title, zone, list, max, otherFull, available, onMove }) => {
  const [over, setOver] = useState(false);
  const dropAt = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    const key = e.dataTransfer.getData('text/plain');
    if (key) onMove(key, zone, index);
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => dropAt(e, undefined)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        padding: '2px 4px',
        borderRadius: 8,
        outline: over ? `2px dashed ${LINK}` : 'none',
      }}
    >
      <span style={{ ...zoneLabel, marginRight: 4 }}>{title}</span>
      {list.length === 0 && <span style={{ fontSize: 13, color: INK_SOFT }}>Ninguna</span>}
      {list.map((k, i) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onDrop={(e) => dropAt(e, i)}>
          {i > 0 && (
            <span aria-hidden style={{ color: INK_SOFT }}>
              ›
            </span>
          )}
          <DimChip dimKey={k} zone={zone} index={i} count={list.length} onMove={onMove} otherZoneFull={otherFull} />
        </span>
      ))}
      <AddDim available={available} disabled={list.length >= max} onAdd={(k) => onMove(k, zone)} />
    </div>
  );
};

/**
 * Banda de pautado (dirección A): dos líneas pegadas a la tabla.
 *   1. Filas y columnas (fichas ordenadas, arrastrables), intercambiar, orden.
 *   2. Medidas, escala de importes y fijar TOTAL.
 */
export const PivotToolbar = ({ config, pinTotal, onPinTotal }) => {
  const {
    rows,
    cols,
    measures,
    available,
    sortBy,
    sortDir,
    sortExplicit,
    moveDimension,
    swapAxes,
    toggleMeasure,
    setSort,
    reprOn,
    reprMeasure,
    setReprMeasure,
    toggleRepr,
  } = config;

  const sortOptions = [
    { label: 'Por defecto (fechas: recientes primero)', value: 'default' },
    { label: 'Etiqueta, A a Z', value: 'label:asc' },
    { label: 'Etiqueta, Z a A', value: 'label:desc' },
    ...measures.flatMap((m) => {
      const lbl = MEASURES.find((x) => x.key === m).label;
      return [
        { label: `${lbl} total, mayor primero`, value: `${m}:desc` },
        { label: `${lbl} total, menor primero`, value: `${m}:asc` },
      ];
    }),
  ];
  const sortValue = sortExplicit ? `${sortBy}:${sortDir}` : 'default';
  if (!sortOptions.some((o) => o.value === sortValue)) {
    // Orden por una medida que ya no está a la vista: se enseña igual.
    const lbl = MEASURES.find((x) => x.key === sortBy)?.label || sortBy;
    sortOptions.push({ label: `${lbl} total, ${sortDir === 'desc' ? 'mayor' : 'menor'} primero`, value: sortValue });
  }

  return (
    <div style={{ background: HEADER_BG, borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px 20px',
          padding: '8px 16px',
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <Zone
          title="Filas"
          zone="rows"
          list={rows}
          max={MAX_ROW_LEVELS}
          otherFull={cols.length >= MAX_COL_LEVELS}
          available={available}
          onMove={moveDimension}
        />
        <button type="button" style={linkBtn} onClick={swapAxes} title="Filas a columnas y columnas a filas">
          ⇄ Intercambiar
        </button>
        <Zone
          title="Columnas"
          zone="cols"
          list={cols}
          max={MAX_COL_LEVELS}
          otherFull={rows.length >= MAX_ROW_LEVELS}
          available={available}
          onMove={moveDimension}
        />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: INK_SOFT }}>Orden:</span>
          <div style={{ minWidth: 220 }}>
            <Select
              label="Orden"
              labelHidden
              options={sortOptions}
              value={sortValue}
              onChange={(v) => {
                const [by, dir = 'asc'] = v.split(':');
                setSort(by, dir);
              }}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 20px', padding: '6px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={zoneLabel}>Medidas</span>
          {MEASURES.map((m) => (
            <Checkbox
              key={m.key}
              label={m.label}
              checked={measures.includes(m.key)}
              disabled={measures.length === 1 && measures.includes(m.key)}
              onChange={() => toggleMeasure(m.key)}
            />
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: INK_SOFT }}>Representación:</span>
            <Select
              label="Medida de la representación"
              labelHidden
              options={MEASURES.map((m) => ({ label: m.label, value: m.key }))}
              value={reprMeasure}
              onChange={setReprMeasure}
            />
            <Checkbox label="Mostrar %" checked={reprOn} onChange={toggleRepr} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: INK_SOFT }}>Importes:</span>
            <Select
              label="Importes"
              labelHidden
              options={MONEY_SCALES.map((s) => ({ label: s.label, value: s.key }))}
              value={config.moneyScale}
              onChange={config.setMoneyScale}
            />
          </div>
          <Checkbox label="Fijar TOTAL" checked={pinTotal} onChange={onPinTotal} />
        </div>
      </div>
    </div>
  );
};
